import React, { useState, useEffect } from 'react';
import { userModel } from '../../../api';
import { sendEventToJumbo } from '../../../helpers/analytics';
import { InvalidSearchImg } from '../../../images/images';
import { EmptyState } from '../../commons';
import { Info } from 'lucide-react';
import { Loader } from './Components.userContext';
import { ToolTip } from '../../ComponentsV1/ToolTip/Tooltip';
import DataCard, {
  DATA_COLOR,
  VIEW,
} from '../../ComponentsV1/DataCard/DataCard';

const blockingDetailsHeadings = [
  'Blocking Status',
  'Blocking Reason',
  'Blocked By',
  'Blocked At',
];

export const UserContext = ({
  orderId = null,
  userId = null,
  currentKarma = {},
  setCustomerDeviceId = () => {},
}) => {
  const [userContext, setUserContext] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getUserContext = () => {
      const queryParams = {};
      queryParams['order_id'] = orderId;
      queryParams['user_id'] = userId;

      userModel
        .getUserContext(queryParams)
        .then((res) => {
          if (!res) return;
          sendEventToJumbo('user_context_api_success', JSON.stringify(res));
          if (res?.isError) {
            return setError(res);
          }
          setUserContext(res);
          setCustomerDeviceId(res?.device_id);
        })
        .catch((err) => {
          sendEventToJumbo('user_context_api_failure');
          setError(err);
        });
    };

    getUserContext();
  }, [orderId, userId, setCustomerDeviceId]);

  if (!userContext && !error) {
    return <Loader />;
  }

  if (error) {
    return (
      <EmptyState
        img={InvalidSearchImg}
        heading={error?.message || 'No data available!'}
        isPop={true}
      />
    );
  }

  const {
    order_karma = {},
    recent_days = 'N/A',
    recent_order_count = 'N/A',
    resolved_complaints_count = 'N/A',
    order_complaint_percentage = '0',
    blocking_details = {},
    cancellation_details = {},
    resolution_summary = {},
    total_aov,
  } = userContext;

  const {
    is_blocked = false,
    reason = '-',
    blocked_by = '-',
    blocked_at = '-',
  } = blocking_details ?? {};

  return (
    <div className='tw-mt-8 tw-flex tw-flex-col tw-gap-6'>
      {/* Current and Order Karma */}
      <DataCard
        stats={[
          {
            title: 'Current Karma',
            value: (
              <div
                style={{
                  backgroundColor: currentKarma?.bgColor,
                  color: currentKarma?.textColor,
                }}
                className='tw-py-2 tw-px-4 tw-rounded-full tw-w-fit tw-font-medium tw-leading-none'
              >
                {currentKarma?.label}
              </div>
            ),
          },
          {
            title: 'Order Karma',
            value: (
              <div
                style={{
                  backgroundColor: order_karma?.karma_btn_color,
                  color: order_karma?.karma_txt_color,
                }}
                className='tw-py-2 tw-px-4 tw-rounded-full tw-w-fit tw-font-medium tw-leading-none'
              >
                {order_karma?.karma}
              </div>
            ),
          },
          {
            title: 'AOV (₹)',
            value: (
              <span className='tw-font-medium tw-text-[1.8rem] tw-leading-none tw-mb-4'>
                {total_aov != null && !isNaN(Number(total_aov))
                  ? Math.round(Number(total_aov))
                  : '-'}
              </span>
            ),
          },
        ]}
      />

      <ComplaintStatistics
        recent_days={recent_days}
        recent_order_count={recent_order_count}
        resolved_complaints_count={resolved_complaints_count}
        order_complaint_percentage={`${
          order_complaint_percentage != null && typeof order_complaint_percentage === 'number'
            ? order_complaint_percentage.toFixed(0)
            : order_complaint_percentage ?? '-'
        }%`}
      />

      <ResolutionStatistics
        recent_days={recent_days}
        resolution_summary={resolution_summary}
      />

      <CancellationSummary
        recent_days={recent_days}
        cancellation_details={cancellation_details}
      />

      {/* Blocking details */}
      {is_blocked && (
        <DataCard
          heading={'Blocking Details'}
          view={VIEW.STAT_ROW}
          content={
            <>
              <div
                className={
                  'tw-flex tw-items-center tw-border-b tw-border tw-border-solid tw-border-grey-200 !tw-border-x-0 tw-bg-blue-100 !tw-border-t-0'
                }
              >
                {blockingDetailsHeadings.map((title) => (
                  <span
                    className={`tw-font-medium tw-text-zinc-500 tw-p-4 ${
                      title === 'Blocking Reason' ? 'tw-w-1/2' : 'tw-w-1/4'
                    }`}
                    key={title}
                  >
                    {title}
                  </span>
                ))}
              </div>
              <div className={'tw-flex tw-items-center'}>
                <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/4'>
                  Blocked
                </span>
                <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-2/4'>
                  {reason}
                </span>
                <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/4'>
                  {blocked_by}
                </span>
                <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/4'>
                  {blocked_at}
                </span>
              </div>
            </>
          }
        />
      )}
    </div>
  );
};

const ComplaintStatistics = ({
  recent_days,
  recent_order_count,
  order_complaint_percentage,
  resolved_complaints_count,
}) => {
  return (
    <DataCard
      heading={`Complaint Stats (last ${recent_days} days)`}
      stats={[
        {
          title: (
            <span className='tw-mb-[2px] tw-flex tw-items-center tw-gap-2 tw-leading-none'>
              Total Orders
            </span>
          ),
          value: recent_order_count,
        },
        {
          title: (
            <span className='tw-mb-[2px] tw-flex tw-items-center tw-gap-2 tw-leading-none'>
              Resolved complaints
            </span>
          ),
          value: resolved_complaints_count,
          color: DATA_COLOR.GREEN,
        },
        {
          title: (
            <>
              <span className='tw-mb-[2px] tw-flex tw-items-center tw-gap-2 tw-leading-none'>
                Complaint percentage
                <ToolTip
                  content={'(Orders with complaint(s) / Total orders) X 100'}
                >
                  <Info
                    size={14}
                    strokeWidth={2.3}
                    className='tw-mt-[0.1rem]'
                  />
                </ToolTip>
              </span>
            </>
          ),
          value: order_complaint_percentage,
          color: DATA_COLOR.RED,
        },
      ]}
    />
  );
};

const CancellationSummary = ({
  recent_days,
  cancellation_details: {
    has_cancellations = false,
    cancellation_count = 0,
    cancellation_amount = 0,
  } = {},
}) => {
  return (
    <DataCard
      view={VIEW.STAT_ROW}
      content={
        <>
          <div
            className={
              'tw-flex tw-items-center tw-border-solid tw-border-[1px] tw-border-grey-200 tw-border-x-0 tw-border-t-0 tw-bg-grey-200/20 !tw-text-[1.3rem] tw-p-5 tw-leading-none'
            }
          >
            <span className='tw-font-medium tw-text-zinc-600 tw-w-1/3 tw-text-[1.3rem]'>
              Cancellations (last {recent_days} days)
            </span>
            {has_cancellations && (
              <span className='tw-font-medium tw-text-zinc-600 tw-w-1/3 tw-text-[1.3rem] tw-pl-2'>
                Count
              </span>
            )}
            {has_cancellations && (
              <span className='tw-font-medium tw-text-zinc-600 tw-w-1/3 tw-text-[1.3rem] tw-pl-5'>
                Total refunded amount (₹)
              </span>
            )}
          </div>
          {has_cancellations ? (
            <div className={'tw-flex tw-items-center '}>
              <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/3  tw-border-r tw-border tw-border-solid tw-border-grey-200 !tw-border-l-0 !tw-border-y-0'>
                Order Cancellations
              </span>
              <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/3  tw-border-r tw-border tw-border-solid tw-border-grey-200 !tw-border-l-0 !tw-border-y-0'>
                {cancellation_count}
              </span>
              <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/3'>
                ₹{cancellation_amount}
              </span>
            </div>
          ) : (
            <div className='tw-p-4 tw-text-zinc-400 tw-text-[1.3rem] tw-font-medium'>
              No order cancellations
            </div>
          )}
        </>
      }
    />
  );
};

const ResolutionStatistics = ({
  recent_days,
  resolution_summary: {
    total_issues_resolved = 0,
    total_resolution_amount = 0,
    resolution_data = {},
  } = {},
}) => {
  return (
    <DataCard
      heading={`Resolution Summary (last ${recent_days} days)`}
      headingRight={
        <span className='tw-text-base-green tw-text-[1.3rem]'>
          {Number(total_issues_resolved) > 1
            ? `${total_issues_resolved} Issues resolved`
            : `${total_issues_resolved} Issue resolved`}
          {` | Total resolution value: ₹${total_resolution_amount}`}
        </span>
      }
      view={VIEW.STAT_ROW}
      content={
        Object.entries(resolution_data).length > 0 ? (
          <>
            <div
              className={
                'tw-flex tw-items-center tw-border-b tw-border tw-border-solid tw-border-grey-200 !tw-border-x-0 tw-bg-blue-100 !tw-border-t-0'
              }
            >
              <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/3'>
                Complaint type
              </span>
              <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/3'>
                Count
              </span>
              <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/3'>
                Resolution amount (₹)
              </span>
            </div>
            {/* Complaint resolution data */}
            {Object.entries(resolution_data).map(
              ([complaint_text, { count, resolution_amount }]) => (
                <div
                  key={complaint_text}
                  className={
                    'tw-flex tw-items-center tw-border-b tw-border tw-border-solid tw-border-grey-200 !tw-border-x-0 !tw-border-t-0'
                  }
                >
                  <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/3 tw-border-r tw-border tw-border-solid tw-border-grey-200 !tw-border-l-0 !tw-border-y-0'>
                    {complaint_text}
                  </span>
                  <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/3 tw-border-r tw-border tw-border-solid tw-border-grey-200 !tw-border-l-0 !tw-border-y-0'>
                    {count}
                  </span>
                  <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/3'>
                    ₹{resolution_amount}
                  </span>
                </div>
              ),
            )}
            <div
              className={
                'tw-flex tw-items-center tw-bg-grey-100/80 tw-rounded-b-[8px] '
              }
            >
              <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/3 tw-border-r tw-border tw-border-solid tw-border-grey-200 !tw-border-l-0 !tw-border-y-0'>
                Total issues resolved
              </span>
              <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/3 tw-border-r tw-border tw-border-solid tw-border-grey-200 !tw-border-l-0 !tw-border-y-0'>
                {total_issues_resolved}
              </span>
              <span className='tw-font-medium tw-text-zinc-500 tw-p-4 tw-w-1/3'>
                ₹{total_resolution_amount}
              </span>
            </div>
          </>
        ) : (
          <div className='tw-p-4 tw-text-zinc-400 tw-text-[1.3rem] tw-font-medium'>
            No resolutions found
          </div>
        )
      }
    />
  );
};
